import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { GraduationCapIcon, PlusCircleIcon, SearchIcon, FilterIcon, EyeIcon, EditIcon, CopyIcon, ArchiveIcon, ChevronDownIcon, ChevronUpIcon, ListIcon, GridIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getStatusColor } from '../../styles/theme';
// Sample program data
const samplePrograms = [{
  id: 'PRG001',
  name: 'Master of Business Administration',
  type: "Master's",
  duration: '2 Years',
  status: 'Active',
  applications: 48,
  intakePeriods: ['Fall 2023', 'Spring 2024']
}, {
  id: 'PRG002',
  name: 'Bachelor of Computer Science',
  type: "Bachelor's'",
  duration: "4 Years'",
  status: 'Active',
  applications: 112,
  intakePeriods: ['Fall 2023']
}, {
  id: 'PRG003',
  name: 'Diploma in Digital Marketing',
  type: 'Diploma',
  duration: '1 Year',
  status: 'Draft',
  applications: 0,
  intakePeriods: []
}, {
  id: 'PRG004',
  name: 'PhD in Data Science',
  type: 'Doctorate',
  duration: '3 Years',
  status: 'Pending Approval',
  applications: 0,
  intakePeriods: ['Spring 2024']
}, {
  id: 'PRG005',
  name: 'Master of Public Administration',
  type: "Master's",
  duration: '2 Years',
  status: 'Active',
  applications: 27,
  intakePeriods: ['Fall 2023', 'Spring 2024']
}, {
  id: 'PRG006',
  name: 'Bachelor of Business Administration',
  type: "Bachelor's",
  duration: '4 Years',
  status: 'Archived',
  applications: 0,
  intakePeriods: []
}];
const Programs: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<'table' | 'card'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [academicYearFilter, setAcademicYearFilter] = useState('All');
  const [activeTab, setActiveTab] = useState('Active');
  // Dropdown states
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  // Sorting state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  // Available filter options
  const typeOptions = ['All', "Bachelor's", "Master's", 'Doctorate', 'Diploma'];
  const yearOptions = ['All', '2023-2024', '2022-2023', '2021-2022'];
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort indicator component
  const SortIndicator = ({
    field
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-6322" /> : <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-6323" />;
  };
  // Count programs by status
  const programCounts = {
    Active: samplePrograms.filter(p => p.status === 'Active').length,
    'Pending Approval': samplePrograms.filter(p => p.status === 'Pending Approval').length,
    Draft: samplePrograms.filter(p => p.status === 'Draft').length,
    Inactive: samplePrograms.filter(p => p.status === 'Archived').length
  };
  // Filter programs based on search query, filters, and active tab
  const filteredPrograms = samplePrograms.filter(program => {
    // Tab filter
    const matchesTab = activeTab === 'Active' && program.status === 'Active' || activeTab === 'Pending Approval' && program.status === 'Pending Approval' || activeTab === 'Drafts' && program.status === 'Draft' || activeTab === 'Inactive' && program.status === 'Archived';
    // Search filter
    const matchesSearch = searchQuery === '' || program.name.toLowerCase().includes(searchQuery.toLowerCase()) || program.id.toLowerCase().includes(searchQuery.toLowerCase());
    // Type filter
    const matchesType = typeFilter === 'All' || program.type === typeFilter;
    // For academic year filter, we would typically filter by year
    // Since we don't have academic year in our sample data, we'll assume all match if 'All' is selected
    // Otherwise, we'll check if any intake period contains the selected year
    const matchesYear = academicYearFilter === 'All' || program.intakePeriods && program.intakePeriods.some(period => period.includes(academicYearFilter.split('-')[0]));
    return matchesTab && matchesSearch && matchesType && matchesYear;
  });
  // Sort the filtered programs
  const sortedPrograms = [...filteredPrograms].sort((a, b) => {
    // Handle different field types
    switch (sortField) {
      case 'name':
        return sortDirection === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
      case 'type':
        return sortDirection === 'asc' ? a.type.localeCompare(b.type) : b.type.localeCompare(a.type);
      case 'status':
        return sortDirection === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
      case 'applications':
        return sortDirection === 'asc' ? a.applications - b.applications : b.applications - a.applications;
      case 'intakePeriods':
        return sortDirection === 'asc' ? a.intakePeriods.length - b.intakePeriods.length : b.intakePeriods.length - a.intakePeriods.length;
      default:
        return 0;
    }
  });
  // Status badge color mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Archived':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };
  // Handle program click to view details
  const handleViewProgram = programId => {
    navigate(`/university/programs/${programId}`);
  };
  return <Layout title="Programs" persona="University Coordinator" universityName="UAE Business School" data-id="element-6324">
      <div className="mb-6 flex justify-between items-center" data-id="element-6325">
        <div className="flex flex-col" data-id="element-6326">
          <div className="flex border-b border-gray-200 w-full" data-id="element-6327">
            <button onClick={() => setActiveTab('Active')} className={`py-3 px-6 font-medium relative ${activeTab === 'Active' ? 'text-primary-500' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-6328">
              <div className="flex items-center" data-id="element-6329">
                Active
                <span className="ml-2 bg-gray-200 text-gray-800 w-6 h-6 rounded-full flex items-center justify-center text-sm" data-id="element-6330">
                  {programCounts.Active}
                </span>
              </div>
              {activeTab === 'Active' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500" data-id="element-6331"></div>}
            </button>
            <button onClick={() => setActiveTab('Pending Approval')} className={`py-3 px-6 font-medium relative ${activeTab === 'Pending Approval' ? 'text-primary-500' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-6332">
              <div className="flex items-center" data-id="element-6333">
                Pending Approval
                <span className="ml-2 bg-gray-200 text-gray-800 w-6 h-6 rounded-full flex items-center justify-center text-sm" data-id="element-6334">
                  {programCounts['Pending Approval']}
                </span>
              </div>
              {activeTab === 'Pending Approval' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500" data-id="element-6335"></div>}
            </button>
            <button onClick={() => setActiveTab('Drafts')} className={`py-3 px-6 font-medium relative ${activeTab === 'Drafts' ? 'text-primary-500' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-6336">
              <div className="flex items-center" data-id="element-6337">
                Drafts
                <span className="ml-2 bg-gray-200 text-gray-800 w-6 h-6 rounded-full flex items-center justify-center text-sm" data-id="element-6338">
                  {programCounts.Draft}
                </span>
              </div>
              {activeTab === 'Drafts' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500" data-id="element-6339"></div>}
            </button>
            <button onClick={() => setActiveTab('Inactive')} className={`py-3 px-6 font-medium relative ${activeTab === 'Inactive' ? 'text-primary-500' : 'text-gray-600 hover:text-gray-800'}`} data-id="element-6340">
              <div className="flex items-center" data-id="element-6341">Inactive</div>
              {activeTab === 'Inactive' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-500" data-id="element-6342"></div>}
            </button>
          </div>
        </div>
        <button onClick={() => navigate('/university/programs/create')} className="bg-primary-500 text-white px-5 py-3 rounded-md flex items-center font-medium hover:bg-primary-600 transition-colors" data-id="element-6343">
          <PlusCircleIcon className="w-5 h-5 mr-2" data-id="element-6344" />
          Create New Program
        </button>
      </div>
      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-6345">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4" data-id="element-6346">
          <div className="relative flex-grow max-w-md" data-id="element-6347">
            <input type="text" placeholder="Search programs by name or ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#BF9E66] focus:border-transparent" data-id="element-6348" />
            <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-6349" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-6350">
            <button onClick={() => setView('table')} className={`p-2 rounded-md ${view === 'table' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} title="Table View" data-id="element-6351">
              <ListIcon className="w-5 h-5 text-gray-600" data-id="element-6352" />
            </button>
            <button onClick={() => setView('card')} className={`p-2 rounded-md ${view === 'card' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} title="Card View" data-id="element-6353">
              <GridIcon className="w-5 h-5 text-gray-600" data-id="element-6354" />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3" data-id="element-6355">
          {/* Type Filter */}
          <div className="relative" data-id="element-6356">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setTypeDropdownOpen(!typeDropdownOpen);
            setYearDropdownOpen(false);
          }} data-id="element-6357">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-6358" />
              Type: {typeFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-6359" />
            </button>
            {typeDropdownOpen && <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-6360">
                <div className="py-1" data-id="element-6361">
                  {typeOptions.map(type => <button key={type} onClick={() => {
                setTypeFilter(type);
                setTypeDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${typeFilter === type ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6362">
                      {type}
                    </button>)}
                </div>
              </div>}
          </div>
          {/* Academic Year Filter */}
          <div className="relative" data-id="element-6363">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setYearDropdownOpen(!yearDropdownOpen);
            setTypeDropdownOpen(false);
          }} data-id="element-6364">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-6365" />
              Academic Year: {academicYearFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-6366" />
            </button>
            {yearDropdownOpen && <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-6367">
                <div className="py-1" data-id="element-6368">
                  {yearOptions.map(year => <button key={year} onClick={() => {
                setAcademicYearFilter(year);
                setYearDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${academicYearFilter === year ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-6369">
                      {year}
                    </button>)}
                </div>
              </div>}
          </div>
        </div>
      </div>
      {/* Programs Table View */}
      {view === 'table' && <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-6370">
          <div className="overflow-x-auto" data-id="element-6371">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6372">
              <thead className="bg-gray-50" data-id="element-6373">
                <tr data-id="element-6374">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')} data-id="element-6375">
                    <div className="flex items-center" data-id="element-6376">
                      Program Name
                      <SortIndicator field="name" data-id="element-6377" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('type')} data-id="element-6378">
                    <div className="flex items-center" data-id="element-6379">
                      Type / Duration
                      <SortIndicator field="type" data-id="element-6380" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')} data-id="element-6381">
                    <div className="flex items-center" data-id="element-6382">
                      Status
                      <SortIndicator field="status" data-id="element-6383" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('applications')} data-id="element-6384">
                    <div className="flex items-center" data-id="element-6385">
                      Applications
                      <SortIndicator field="applications" data-id="element-6386" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('intakePeriods')} data-id="element-6387">
                    <div className="flex items-center" data-id="element-6388">
                      Intake Periods
                      <SortIndicator field="intakePeriods" data-id="element-6389" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6390">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6391">
                {sortedPrograms.length > 0 ? sortedPrograms.map(program => <tr key={program.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewProgram(program.id)} data-id="element-6392">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6393">
                        <div className="flex items-center" data-id="element-6394">
                          <div className={`flex-shrink-0 h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center`} data-id="element-6395">
                            <GraduationCapIcon className={`h-5 w-5 text-primary-500`} data-id="element-6396" />
                          </div>
                          <div className="ml-4" data-id="element-6397">
                            <div className="text-sm font-medium text-gray-900" data-id="element-6398">
                              {program.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6399">
                              {program.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6400">
                        <div className="text-sm text-gray-900" data-id="element-6401">
                          {program.type}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-6402">
                          {program.duration}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6403">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(program.status)}`} data-id="element-6404">
                          {program.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6405">
                        {program.applications > 0 ? <a href="#" className="text-primary-500 hover:underline" onClick={e => e.stopPropagation()} data-id="element-6406">
                            {program.applications} applications
                          </a> : <span data-id="element-6407">No applications</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6408">
                        {program.intakePeriods.length > 0 ? <div className="flex flex-col" data-id="element-6409">
                            {program.intakePeriods.map((intake, index) => <span key={index} data-id="element-6410">{intake}</span>)}
                          </div> : <span data-id="element-6411">No active intakes</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={e => e.stopPropagation()} data-id="element-6412">
                        <div className="flex space-x-2" data-id="element-6413">
                          <button className="text-gray-500 hover:text-gray-700" title="View Program Details" onClick={e => {
                    e.stopPropagation();
                    handleViewProgram(program.id);
                  }} data-id="element-6414"></button>
                          <button className="text-gray-500 hover:text-gray-700" title="Edit Program" data-id="element-6415">
                            <EditIcon className="h-5 w-5" data-id="element-6416" />
                          </button>
                          <button className="text-gray-500 hover:text-gray-700" title="Duplicate Program" data-id="element-6417">
                            <CopyIcon className="h-5 w-5" data-id="element-6418" />
                          </button>
                          <button className="text-gray-500 hover:text-gray-700" title="Archive Program" data-id="element-6419">
                            <ArchiveIcon className="h-5 w-5" data-id="element-6420" />
                          </button>
                        </div>
                      </td>
                    </tr>) : <tr data-id="element-6421">
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-6422">
                      No programs found matching your criteria
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Programs Card View */}
      {view === 'card' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6423">
          {filteredPrograms.length > 0 ? filteredPrograms.map(program => <div key={program.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewProgram(program.id)} data-id="element-6424">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-6425">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(program.status)}`} data-id="element-6426">
                    {program.status}
                  </span>
                  <div className="flex space-x-1" onClick={e => e.stopPropagation()} data-id="element-6427">
                    <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" title="View Program Details" onClick={e => {
              e.stopPropagation();
              handleViewProgram(program.id);
            }} data-id="element-6428">
                      <EyeIcon className="h-4 w-4" data-id="element-6429" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" title="Edit Program" data-id="element-6430">
                      <EditIcon className="h-4 w-4" data-id="element-6431" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" title="Duplicate Program" data-id="element-6432">
                      <CopyIcon className="h-4 w-4" data-id="element-6433" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100" title="Archive Program" data-id="element-6434">
                      <ArchiveIcon className="h-4 w-4" data-id="element-6435" />
                    </button>
                  </div>
                </div>
                <div className="p-4" data-id="element-6436">
                  <div className="flex items-center mb-3" data-id="element-6437">
                    <div className={`flex-shrink-0 h-10 w-10 rounded-full bg-primary-50 flex items-center justify-center`} data-id="element-6438">
                      <GraduationCapIcon className={`h-5 w-5 text-primary-500`} data-id="element-6439" />
                    </div>
                    <div className="ml-3" data-id="element-6440">
                      <h3 className="text-sm font-medium text-gray-900" data-id="element-6441">
                        {program.name}
                      </h3>
                      <div className="text-xs text-gray-500" data-id="element-6442">{program.id}</div>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-6443">
                    <div className="text-xs text-gray-500" data-id="element-6444">Type / Duration</div>
                    <div className="text-sm text-gray-900" data-id="element-6445">
                      {program.type} • {program.duration}
                    </div>
                  </div>
                  <div className="flex justify-between mb-3" data-id="element-6446">
                    <div data-id="element-6447">
                      <div className="text-xs text-gray-500" data-id="element-6448">Applications</div>
                      <div className="text-sm text-gray-900" data-id="element-6449">
                        {program.applications > 0 ? <a href="#" className="text-primary-500 hover:underline" onClick={e => e.stopPropagation()} data-id="element-6450">
                            {program.applications} applications
                          </a> : <span data-id="element-6451">No applications</span>}
                      </div>
                    </div>
                    <div data-id="element-6452">
                      <div className="text-xs text-gray-500" data-id="element-6453">
                        Intake Periods
                      </div>
                      <div className="text-sm text-gray-900" data-id="element-6454">
                        {program.intakePeriods.length > 0 ? <span data-id="element-6455">{program.intakePeriods.length} active</span> : <span data-id="element-6456">None</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>) : <div className="col-span-full bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center" data-id="element-6457">
              <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-6458">
                <GraduationCapIcon className="w-6 h-6 text-gray-400" data-id="element-6459" />
              </div>
              <h3 className="text-gray-500 font-medium mb-2" data-id="element-6460">
                No programs found
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-6461">
                Try adjusting your filters or create a new program
              </p>
            </div>}
        </div>}
    </Layout>;
};
export default Programs;